import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import getMemberInitials from '../../utils/getMemberInitials';
import getProfileSlug from '../../utils/getProfileSlug';
import './BrotherCard.scss';

/**
 * Shared layout for member / board / alumni cards — a single, fully
 * keyboard-and-screen-reader-accessible link styled as an archive record.
 * @param {object} props
 * @param {string} props.name
 * @param {string} [props.headshot]
 * @param {'/members'|'/alumni'} props.routePrefix
 * @param {React.ReactNode} [props.subtitle] - second line (major, position, etc.)
 * @param {React.ReactNode} [props.eyebrow] - small label above the name (e.g. board title)
 */
function BrotherCardBase({ name, headshot, routePrefix, subtitle, eyebrow }) {
  const location = useLocation();
  const history = useHistory();

  // Defensive: never render a card for a blank/placeholder record.
  if (!name || !name.trim()) return null;

  const path = `${routePrefix}/${getProfileSlug(name)}`;
  const missingAvatar = !headshot;

  // Only hijack an unmodified primary-button click aimed at this same
  // browsing context — everything else (modifier/middle click, or an
  // explicit non-_self target) must fall through to native Link/anchor
  // behavior so it opens exactly as the browser would for any other link.
  function handleClick(event) {
    const isModified =
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      event.shiftKey;
    const target = event.currentTarget.getAttribute('target');
    if (isModified || (target && target !== '_self')) return;

    event.preventDefault();
    history.push(path, { background: location, backgroundScrollY: window.scrollY });
  }

  return (
    <Link
      className={
        missingAvatar ? 'BrotherCard BrotherCard--missing-avatar' : 'BrotherCard'
      }
      to={path}
      onClick={handleClick}
    >
      <span className="BrotherCard-frame">
        {headshot ? (
          <img className="BrotherImg" src={headshot} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className="BrotherImg InitialBubble" aria-hidden="true">
            <span className="InitialsRing">
              <span className="Initials">{getMemberInitials(name)}</span>
            </span>
          </span>
        )}
      </span>
      {eyebrow ? <span className="BrotherEyebrow">{eyebrow}</span> : null}
      <span className="BrotherName">{name}</span>
      {subtitle ? <span className="BrotherYearMajor">{subtitle}</span> : null}
    </Link>
  );
}

export default BrotherCardBase;
